import PopUpWarning from '@components/PopUpWarning';
import { TypeReturn } from '@components/styled-autocomplete/useStyledAutocomplete';
import { pushError, pushSuccess } from '@components/toast';
import { appConfig } from '@configs/index';
import { permitExemptHttpController, potentialGroupContraventionController } from '@controllers/index';
import { ListProps } from '@Core';
import { Camera, CancellationReasons, GeoZones, GroupPotentialContraventions, PCNDuplicate } from '@EcamModel/model';
import usePopUp, { UsePopUpReturnType } from '@hooks/usePopUp';
import { Box, Button, Dialog, Stack, SxProps, TextField, Theme, Typography } from '@mui/material';
import { RequiredTitle } from '@pages/cameras/add-new';
import color from '@theme/Colors';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { TransformComponent, TransformWrapper } from 'react-zoom-pan-pinch';
import MyModal from '../hooks/MyModal';
import { KPopup } from '../hooks/useModal';
import { Controls } from './EvidencePhotoGroup';
import ExemptFutureEnforcementNotice, {
    EXEMPT_REASON_OPTIONS,
    ExemptFutureEnforcementValue,
} from './ExemptFutureEnforcementNotice';
import { CustomTextField } from './ModalChangePlate';
import ReasonChipSelect from './ReasonChipSelect';
import { PermitExempt, ScopeType } from '@EcamModel/model/PermitExempt';
import { useMutation, useQueryClient } from 'react-query';
import { AxiosError } from '@controllers/axiosClient';
import { VehicleExemptTag } from '@EcamModel/model/VehicleExempt';

type Props = KPopup & {
    cancellationReasonAutocomplete: TypeReturn<CancellationReasons, ListProps<CancellationReasons>>;
    handleCancelPC: (cancelReason: FormCancelReasonValues) => void;
    popUpSplitAndComplete: UsePopUpReturnType;
    commentReason: (comment: string) => void;
    groupPc: GroupPotentialContraventions | undefined;
    isEVWarning?: boolean;
    selectedCamera?: Camera;
    preSelectedReasonIndex?: number;
    onResetPreSelectedIndex?: () => void;
};

export interface FormCancelReasonValues {
    cancellationReason: CancellationReasons;
    comment: string;
    data: VehicleInfoCardProps | undefined;
    exemption?: ExemptFutureEnforcementValue;
}
interface VehicleInfoCardProps {
    id: number;
    reference: string | undefined;
    source: string | undefined;
    issueDate: Date;
    licensePlate: string;
    imageUrl?: string;
}

export const cancellationReasonEVCars = appConfig.contraventionReasonId.cancellationReasonEVCars;

export default function ModalCancellationReasons(props: Props) {
    const { commentReason, groupPc, selectedCamera } = props;
    const queryClient = useQueryClient();

    const {
        handleSubmit,
        watch,
        reset,
        control,
        setValue,
        getValues,
        setError,
        clearErrors,
        formState: { isDirty, isValid, errors, touchedFields },
    } = useForm<FormCancelReasonValues>({
        mode: 'all',
        defaultValues: {
            cancellationReason: undefined,
            comment: '',
            data: undefined,
        },
    });
    const [isCheckReasonMismatch, setIsCheckReasonMismatch] = useState<boolean>(false);
    const [selectedReason, setSelectedReason] = useState<CancellationReasons | undefined>(undefined);

    const handleReasonChange = (reason: CancellationReasons | undefined) => {
        setSelectedReason(reason);
        setValue('cancellationReason', reason as any, {
            shouldValidate: true,
            shouldTouch: true,
            shouldDirty: true,
        });
        if (props.preSelectedReasonIndex != null) {
            props.onResetPreSelectedIndex?.();
        }
    };

    useEffect(() => {
        if (!props.open) return;
        setIsCheckReasonMismatch(false);

        const reasons = props?.cancellationReasonAutocomplete?.paging?.rows || [];
        const evReason = reasons.find((d) => d.Reason === cancellationReasonEVCars);

        if (props.preSelectedReasonIndex != null && props.preSelectedReasonIndex >= 0) {
            const targetReason = reasons[props.preSelectedReasonIndex];
            if (targetReason) {
                setSelectedReason(targetReason);
                reset({
                    cancellationReason: targetReason,
                    comment: '',
                });
                setValue('cancellationReason', targetReason, {
                    shouldValidate: true,
                    shouldTouch: true,
                    shouldDirty: true,
                });
                return;
            }
        }

        const initialReason = props.isEVWarning ? evReason : undefined;
        setSelectedReason(initialReason);
        reset({
            cancellationReason: initialReason,
            comment: '',
        });
    }, [props.open, props.isEVWarning, props.preSelectedReasonIndex]);

    useEffect(() => {
        if (watch()?.cancellationReason?.Id) {
            const filteredReasonMismatch = props?.cancellationReasonAutocomplete?.paging?.rows.some(
                (d) => watch().cancellationReason.Id === 2
            );
            setIsCheckReasonMismatch(filteredReasonMismatch);
        }
    }, [watch()?.cancellationReason?.Id]);

    useEffect(() => {
        const currentReason = watch()?.cancellationReason;
        if (currentReason?.Reason === 'Client Instructions') {
            setExemptValue((prev) => ({
                ...prev,
                VehicleExemptTag: prev.IsEnabled ? VehicleExemptTag.ClientRequest : null,
            }));
        } else if (currentReason?.Reason === 'Emergency Vehicles') {
            setExemptValue({
                IsEnabled: true,
                VehicleExemptTag: VehicleExemptTag.EmergencyVehicle,
            });
        }
    }, [watch()?.cancellationReason?.Reason]);

    const [isLoading, setIsLoading] = useState<boolean>(false);

    const [openImagePreview, setOpenImagePreview] = useState<boolean>(false);
    const [selectImagePreview, setSelectImagePreview] = useState<string>('');
    const [isCheckReasonDuplicates, setIsCheckReasonDuplicates] = useState<boolean>(false);

    const popUpNoItemsSelectedWarning = usePopUp();

    const [searchVrnPlate, setSearchVrnPlate] = useState<string>('');
    const [valueVrnPlate, setValueVrnPlate] = useState<string | undefined>('');
    const [selectedColumnEvidencePhoto, setSelectedColumnEvidencePhoto] = useState<number | null>(null);
    const [listDuplicates, setListDuplicates] = useState<PCNDuplicate[]>([]);
    const [exemptValue, setExemptValue] = useState<ExemptFutureEnforcementValue>({
        IsEnabled: false,
        VehicleExemptTag: null,
    });
    const commentInputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

    const handleRadioChangeColumnEvidencePhoto = (
        event: React.ChangeEvent<HTMLInputElement>,
        data: VehicleInfoCardProps
    ) => {
        setSelectedColumnEvidencePhoto(Number(event.target.value));
        setValue('data', data);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const inputValue = event.target.value;
        setSearchVrnPlate(inputValue.toUpperCase());
    };

    useEffect(() => {
        if (!props.open) return;
        // getItemsDuplicate();
    }, [props.open]);

    const getItemsDuplicate = async (searchValue?: string) => {
        setIsLoading(true);
        await potentialGroupContraventionController
            .getGroupPotentialContraventionDuplications({
                GroupPotentialContraventionId: groupPc?.Id!,
                Plate: searchValue ?? undefined,
            })
            .then((res) => {
                setListDuplicates(res);
            })
            .catch((err) => {
                pushError(err.response.data.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleSearchReg = (searchValue: string) => {
        setValueVrnPlate(searchValue);
        searchValue ? getItemsDuplicate(searchValue) : getItemsDuplicate(undefined);
    };

    const handleCloseModal = () => {
        props.handleClose();
        clearErrors();
        setSelectedReason(undefined);
        reset({
            cancellationReason: undefined,
            comment: '',
            data: undefined,
        });
        setSearchVrnPlate('');
        setIsCheckReasonDuplicates(false);
        setSelectedColumnEvidencePhoto(null);
        setExemptValue({ IsEnabled: false, VehicleExemptTag: null });
        props.onResetPreSelectedIndex?.();
    };

    const digitBufferRef = useRef<string>('');
    const digitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const selectReasonByIndex = useCallback(
        (index: number) => {
            const reasons = props?.cancellationReasonAutocomplete?.paging?.rows || [];
            if (index >= 0 && index < reasons.length) {
                const reason = reasons[index];
                setSelectedReason(reason);
                setValue('cancellationReason', reason as any, {
                    shouldValidate: true,
                    shouldTouch: true,
                    shouldDirty: true,
                });
            }
        },
        [props?.cancellationReasonAutocomplete?.paging?.rows, setValue]
    );

    const handleNumberKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!props.open) return;
            if (e.altKey) return;

            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
            if (e.key < '0' || e.key > '9') return;

            e.preventDefault();
            e.stopImmediatePropagation();

            if (digitTimerRef.current) {
                clearTimeout(digitTimerRef.current);
                digitTimerRef.current = null;
            }

            digitBufferRef.current += e.key;

            if (digitBufferRef.current.length >= 2) {
                const num = parseInt(digitBufferRef.current, 10);
                selectReasonByIndex(num);
                digitBufferRef.current = '';
            } else {
                digitTimerRef.current = setTimeout(() => {
                    const num = parseInt(digitBufferRef.current, 10);
                    if (num >= 0) {
                        selectReasonByIndex(num);
                    }
                    digitBufferRef.current = '';
                    digitTimerRef.current = null;
                }, 500);
            }
        },
        [props.open, selectReasonByIndex]
    );

    useEffect(() => {
        if (!props.open) {
            digitBufferRef.current = '';
            if (digitTimerRef.current) {
                clearTimeout(digitTimerRef.current);
                digitTimerRef.current = null;
            }
            return;
        }
        document.addEventListener('keydown', handleNumberKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleNumberKeyDown, { capture: true });
    }, [props.open, handleNumberKeyDown]);

    useEffect(() => {
        return () => {
            if (digitTimerRef.current) {
                clearTimeout(digitTimerRef.current);
                digitTimerRef.current = null;
            }
            digitBufferRef.current = '';
        };
    }, []);

    const handleCancelPC = () => {
        props.handleCancelPC(getValues());
        handleCloseModal();
        setValueVrnPlate(undefined);
        popUpNoItemsSelectedWarning.onClose?.();
    };

    const upsertExemptPermitAPI = async (_exemptPermit: PermitExempt) => {
        const response = await permitExemptHttpController.upsert(_exemptPermit);
        return response;
    };

    const mutationUpsertExemptPermit = useMutation(upsertExemptPermitAPI, {
        onSuccess: async (res, variables) => {
            queryClient.invalidateQueries(['listExemptPermits']);
        },
        onError: (err: AxiosError) => pushError(err.message),
        onSettled: () => {},
    });

    const handleCreateExemption = async () => {
        const _filter: PermitExempt = {
            ContraventionReasonId: groupPc?.PotentialContraventions?.[0]?.ContraventionReasonId,
            ScopeType: ScopeType.GeoZone,
            ScopeId: selectedCamera?.GeoZones?.[0].Id,
            VehicleExempts: [
                {
                    Plate: groupPc?.Plate!,
                    VehicleExemptTag: exemptValue.VehicleExemptTag!,
                },
            ],
        };
        await mutationUpsertExemptPermit.mutateAsync(_filter).finally(() => {
            handleCloseModal();
        });
    };

    const groupPcPotentialContravention = groupPc?.PotentialContraventions?.[0];

    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!props.open) return;

            if (e.key === 'Enter' && isValid && !isLoading) {
                e.preventDefault();
                e.stopImmediatePropagation();
                handleCancelPC();
                if (exemptValue.IsEnabled) {
                    handleCreateExemption();
                }
            }
        },
        [props.open, isValid, isLoading, exemptValue, handleCancelPC, handleCreateExemption]
    );

    useEffect(() => {
        if (!props.open) return;
        document.addEventListener('keydown', handleKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
    }, [props.open, handleKeyDown]);

    const handleExemptKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (!props.open) return;
            const target = e.target as HTMLElement;
            if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return;
            if (!e.altKey) return;

            if (e.key === 'e' || e.key === 'E') {
                e.preventDefault();
                e.stopPropagation();
                setExemptValue((prev) => ({
                    ...prev,
                    IsEnabled: !prev.IsEnabled,
                    VehicleExemptTag: !prev.IsEnabled ? (prev.VehicleExemptTag ?? EXEMPT_REASON_OPTIONS[0]) : null,
                }));
                return;
            }
            if (e.key === 'c' || e.key === 'C') {
                e.preventDefault();
                e.stopPropagation();
                commentInputRef.current?.focus();
                return;
            }
            if (e.key >= '1' && e.key <= '7') {
                const index = parseInt(e.key, 10);
                const option = EXEMPT_REASON_OPTIONS[index - 1];
                if (option) {
                    e.preventDefault();
                    e.stopPropagation();
                    setExemptValue((prev) => ({
                        ...prev,
                        IsEnabled: true,
                        VehicleExemptTag: option,
                    }));
                }
            }
        },
        [props.open]
    );

    useEffect(() => {
        if (!props.open) return;
        document.addEventListener('keydown', handleExemptKeyDown, { capture: true });
        return () => document.removeEventListener('keydown', handleExemptKeyDown, { capture: true });
    }, [props.open, handleExemptKeyDown]);

    return (
        <MyModal
            {...props}
            handleClose={() => props.handleClose()}
            modalContent={
                <Stack flexBasis={1} p={'24px'} gap={3}>
                    <Typography variant="h4" textAlign={'center'} sx={{ color: color.danger }}>
                        Cancel
                    </Typography>
                    <Typography textAlign={'center'}>Please select the cancellation reasons.</Typography>

                    <Controller
                        name="cancellationReason"
                        control={control}
                        rules={{
                            required: {
                                value: true,
                                message: 'Please select a reason.',
                            },
                        }}
                        render={({ field, fieldState: { error } }) => (
                            <ReasonChipSelect
                                value={selectedReason}
                                onChange={handleReasonChange}
                                reasons={props.cancellationReasonAutocomplete?.paging?.rows || []}
                            />
                        )}
                    />

                    {/* <Collapse in={isCheckReasonDuplicates}>
                        {isLoading ? (
                            <Stack direction="row" justifyContent="center" alignItems="center" minHeight={200}>
                                <CircularProgress />
                            </Stack>
                        ) : (
                            <>
                                {isCheckReasonDuplicates && (
                                    <>
                                        {listDuplicates?.length > 0 ? (
                                            <Stack direction={'column'} spacing={1}>
                                                <Controller
                                                    control={control}
                                                    name={'data.licensePlate'}
                                                    rules={{
                                                        required: {
                                                            value: true,
                                                            message: 'Please select a plate.',
                                                        },
                                                    }}
                                                    render={({ field, fieldState: { error } }) => (
                                                        <TextField
                                                            {...field}
                                                            label={'Plate'}
                                                            required
                                                            placeholder={'Enter plate'}
                                                            onChange={(e) => {
                                                                field.onChange(e.target.value);
                                                                setSelectedColumnEvidencePhoto(null);
                                                                setValue('data.reference', undefined);
                                                                setValue('data.source', undefined);
                                                            }}
                                                            inputProps={{
                                                                maxLength: 500,
                                                            }}
                                                            sx={{
                                                                '&.MuiInputBase-input': { p: '0px' },
                                                                '& .MuiOutlinedInput-input': { p: '0px !important' },
                                                            }}
                                                            error={!!error?.message}
                                                            helperText={error?.message}
                                                            multiline
                                                            minRows={1}
                                                            fullWidth
                                                            size="small"
                                                        />
                                                    )}
                                                />
                                                <BaseSearchInputComponent
                                                    handleInputChange={handleInputChange}
                                                    handleSearchReg={() => {
                                                        handleSearchReg(searchVrnPlate);
                                                    }}
                                                    searchVrnPlate={searchVrnPlate}
                                                    style={{
                                                        bgcolor: color.grey100,
                                                        padding: '8px 16px',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                                <Grid
                                                    container
                                                    spacing={0.5}
                                                    sx={{
                                                        maxHeight: 310,
                                                        overflowY: 'auto',
                                                        '::-webkit-scrollbar': { width: '2px' },
                                                    }}
                                                >
                                                    {listDuplicates?.map((item, index) => (
                                                        <Grow
                                                            in={true}
                                                            key={index}
                                                            timeout={Math.min(500 * index, 1000)}
                                                        >
                                                            <Grid item md={4} sm={6} xs={12}>
                                                                <Stack
                                                                    bgcolor={color.white}
                                                                    p={1}
                                                                    border={`1px solid ${color.grey100}`}
                                                                    borderRadius={1}
                                                                    direction={'column'}
                                                                    alignItems={'start'}
                                                                >
                                                                    <FormControl>
                                                                        <RadioGroup
                                                                            sx={{ ml: 0.5 }}
                                                                            row
                                                                            aria-labelledby="radio-buttons-group-label"
                                                                            name="radio-buttons-group"
                                                                            value={selectedColumnEvidencePhoto}
                                                                            onChange={(
                                                                                event: React.ChangeEvent<HTMLInputElement>
                                                                            ) => {
                                                                                handleRadioChangeColumnEvidencePhoto(
                                                                                    event,
                                                                                    {
                                                                                        id: item.id,
                                                                                        licensePlate:
                                                                                            valueVrnPlate ||
                                                                                            groupPc?.Plate!,
                                                                                        issueDate:
                                                                                            item?.firstIssuedDate!,
                                                                                        source: item.source,
                                                                                        reference: item.reference ?? '',
                                                                                    }
                                                                                );
                                                                                setValue(
                                                                                    'data.licensePlate',
                                                                                    valueVrnPlate || groupPc?.Plate!,
                                                                                    {
                                                                                        shouldValidate: true,
                                                                                        shouldTouch: true,
                                                                                        shouldDirty: true,
                                                                                    }
                                                                                );
                                                                            }}
                                                                        >
                                                                            <Grid container xs={12}>
                                                                                <Grid>
                                                                                    <FormControlLabel
                                                                                        sx={{
                                                                                            '.MuiFormControlLabel-label':
                                                                                                {
                                                                                                    mt: '3px !important',
                                                                                                },
                                                                                        }}
                                                                                        control={
                                                                                            <Radio value={item.id} />
                                                                                        }
                                                                                        label={item.id}
                                                                                    />
                                                                                </Grid>
                                                                            </Grid>
                                                                        </RadioGroup>
                                                                    </FormControl>
                                                                    {item?.blobName ? (
                                                                        <Box
                                                                            sx={{
                                                                                width: '100%',
                                                                                height: '150px',
                                                                                background: `url(${appConfig.gateway.blobURL}${item.blobName}) no-repeat center center`,
                                                                                backgroundSize: 'cover',
                                                                                borderRadius: 1,
                                                                                cursor: 'pointer',
                                                                            }}
                                                                            onClick={() => {
                                                                                setSelectImagePreview(
                                                                                    `${appConfig.gateway.blobURL}${item.blobName}`
                                                                                );
                                                                                setOpenImagePreview(true);
                                                                            }}
                                                                        />
                                                                    ) : (
                                                                        <ListNoData changeText="No image" />
                                                                    )}

                                                                    <Stack direction={'column'} width={'100%'}>
                                                                        <Box
                                                                            bgcolor={'#FFFF00'}
                                                                            borderRadius={1}
                                                                            border={`1px solid ${color.textPrimary}`}
                                                                            textAlign={'center'}
                                                                            fontSize={24}
                                                                            fontWeight={500}
                                                                            my={1}
                                                                        >
                                                                            {valueVrnPlate?.toUpperCase() ||
                                                                                groupPc?.Plate.toUpperCase()}
                                                                        </Box>
                                                                        <Typography variant="body2">
                                                                            Issued date:{' '}
                                                                            {formatUKDate(item?.firstIssuedDate!)}
                                                                        </Typography>
                                                                        <Typography variant="body2">
                                                                            Source: {item.source}
                                                                        </Typography>
                                                                        {item.source === 'ECam' && (
                                                                            <Typography variant="body2">
                                                                                Step: {item.step}
                                                                            </Typography>
                                                                        )}
                                                                        <Typography variant="body2">
                                                                            Status: {statusMap[item.status]}
                                                                        </Typography>
                                                                    </Stack>
                                                                </Stack>
                                                            </Grid>
                                                        </Grow>
                                                    ))}
                                                </Grid>
                                            </Stack>
                                        ) : (
                                            <>
                                                <BaseSearchInputComponent
                                                    handleInputChange={handleInputChange}
                                                    handleSearchReg={() => {
                                                        handleSearchReg(searchVrnPlate);
                                                    }}
                                                    searchVrnPlate={searchVrnPlate}
                                                />

                                                <Stack
                                                    direction={'column'}
                                                    alignItems={'center'}
                                                    spacing={1}
                                                    border={'1px solid #DDDDDD'}
                                                    borderRadius={1}
                                                    my={2}
                                                >
                                                    <EmptyIcon />
                                                    <Stack textAlign={'center'} direction={'column'}>
                                                        <Typography variant="body1">
                                                            We currently cannot find any IDs related to the duplication
                                                            of this PC.
                                                        </Typography>
                                                        <Typography variant="body1">
                                                            Please check carefully and enter the exact license plate
                                                            number you think is duplicate.
                                                        </Typography>
                                                    </Stack>

                                                    <Controller
                                                        control={control}
                                                        name={'data.licensePlate'}
                                                        rules={{
                                                            required: {
                                                                value: true,
                                                                message: 'Please select a plate.',
                                                            },
                                                        }}
                                                        render={({ field, fieldState: { error } }) => (
                                                            <CustomTextField
                                                                {...field}
                                                                placeholder={'Enter plate'}
                                                                autoFocus
                                                                disabled={false}
                                                                inputProps={{
                                                                    maxLength: 20,
                                                                }}
                                                                style={{
                                                                    fontSize: 14,
                                                                    width: '80%',
                                                                }}
                                                                error={!!error?.message}
                                                                helperText={error?.message}
                                                                onChange={(e) => {
                                                                    field.onChange(e.target.value);
                                                                    setSelectedColumnEvidencePhoto(null);
                                                                    setValue('data.reference', undefined);
                                                                    setValue('data.source', undefined);
                                                                }}
                                                            />
                                                        )}
                                                    />
                                                </Stack>
                                            </>
                                        )}
                                    </>
                                )}
                            </>
                        )}
                    </Collapse> */}

                    <Controller
                        control={control}
                        name={'comment'}
                        render={({ field }) => (
                            <TextField
                                {...field}
                                inputRef={(el) => {
                                    (field.ref as (el: HTMLInputElement | HTMLTextAreaElement | null) => void)?.(el);
                                    commentInputRef.current = el;
                                }}
                                label={'Comment'}
                                placeholder={'Enter comment'}
                                onChange={(e) => {
                                    field.onChange(e.target.value);
                                    commentReason(e.target.value);
                                }}
                                inputProps={{
                                    maxLength: 500,
                                }}
                                sx={{
                                    '&.MuiInputBase-input': { p: '0px' },
                                    '& .MuiOutlinedInput-input': { p: '0px !important' },
                                }}
                                multiline
                                minRows={4}
                                fullWidth
                                size="small"
                            />
                        )}
                    />

                    <ExemptFutureEnforcementNotice
                        locationName={groupPcPotentialContravention?.Zone?.Location?.Name!}
                        zoneNames={groupPcPotentialContravention?.Zone?.PublicName!}
                        cameraName={groupPcPotentialContravention?.Camera?.Name!}
                        cameraId={groupPcPotentialContravention?.Camera?.Id!}
                        geoZone={groupPcPotentialContravention?.Camera?.GeoZones?.[0] || ({} as GeoZones)}
                        value={exemptValue}
                        onChange={setExemptValue}
                    />

                    {selectImagePreview && (
                        <Dialog
                            open={!!openImagePreview}
                            onClose={() => {
                                setOpenImagePreview(false);
                                setSelectImagePreview('');
                            }}
                            PaperProps={{
                                sx: {
                                    minWidth: 1280,
                                    minHeight: 720,
                                },
                            }}
                        >
                            <TransformWrapper
                                alignmentAnimation={{ sizeX: 0, sizeY: 0 }}
                                doubleClick={{ disabled: true }}
                                wheel={{ disabled: false }}
                                centerZoomedOut={true}
                            >
                                {({ zoomIn, zoomOut, resetTransform, ...rest }) => (
                                    <>
                                        <Controls />
                                        <TransformComponent>
                                            <Stack sx={{ position: 'relative' }}>
                                                <Box
                                                    sx={{
                                                        width: 1280,
                                                        height: 720,
                                                        background: `url(${selectImagePreview}) no-repeat center center`,
                                                        backgroundSize: 'cover',
                                                        borderRadius: 1,
                                                    }}
                                                />
                                            </Stack>
                                        </TransformComponent>
                                    </>
                                )}
                            </TransformWrapper>
                        </Dialog>
                    )}

                    <PopUpWarning
                        {...popUpNoItemsSelectedWarning}
                        title="Confirm"
                        message="Are you sure you want to cancel this PC with a manually entered 
                        license plate number and that is not on our suggested list?"
                        onConfirm={handleCancelPC}
                    />

                    <Stack direction="row" justifyContent={'space-between'}>
                        <Button variant={'cancel'} onClick={handleCloseModal} sx={{ minWidth: '200px' }}>
                            Close
                        </Button>
                        <Stack direction={'row'} spacing={2} alignItems={'center'}>
                            {isCheckReasonMismatch && (
                                <Button
                                    variant={'errorOutlined'}
                                    onClick={() => {
                                        props.popUpSplitAndComplete?.setTrue();
                                        handleCloseModal();
                                    }}
                                    sx={{ minWidth: '200px' }}
                                    disabled={!isValid}
                                >
                                    Cancel & split
                                </Button>
                            )}

                            <Button
                                variant={'errorContained'}
                                onClick={() => {
                                    // if (
                                    //     isCheckReasonDuplicates &&
                                    //     selectedColumnEvidencePhoto === null &&
                                    //     listDuplicates?.length
                                    // ) {
                                    //     popUpNoItemsSelectedWarning.setTrue();
                                    // } else {
                                    handleCancelPC();
                                    if (exemptValue.IsEnabled) {
                                        handleCreateExemption();
                                    }
                                    // }
                                }}
                                sx={{ minWidth: '200px' }}
                                disabled={!isValid || isLoading}
                            >
                                {exemptValue.IsEnabled ? 'Cancel & Exempt' : 'Cancel'}
                            </Button>
                        </Stack>
                    </Stack>
                </Stack>
            }
            width={1150}
        />
    );
}
interface BaseComponentProps {
    searchVrnPlate: string;
    handleInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleSearchReg: (searchValue: string) => void;
    style?: SxProps<Theme>;
}

const BaseSearchInputComponent: React.FC<BaseComponentProps> = ({
    searchVrnPlate,
    handleInputChange,
    handleSearchReg,
    style,
}) => {
    return (
        <Stack direction="column" spacing={1} sx={style}>
            <RequiredTitle sx={{ width: 'max-content', lineHeight: 2 }} title="Please select ID duplicate" required />
            <Stack mb={2} width="100%" direction="row" alignItems="center">
                <Stack flex={1} marginRight={2}>
                    <CustomTextField
                        value={searchVrnPlate}
                        onChange={handleInputChange}
                        placeholder="Search Reg"
                        autoFocus
                        disabled={false}
                        inputProps={{ maxLength: 20 }}
                        style={{ fontSize: 14 }}
                    />
                </Stack>
                <Stack>
                    <Button variant="contained" sx={{ minWidth: 150 }} onClick={() => handleSearchReg(searchVrnPlate)}>
                        Search
                    </Button>
                </Stack>
            </Stack>
        </Stack>
    );
};
